import React, { useState } from 'react'
import {
  Button,
  Checkbox,
  LabeledInput,
  LabeledSelect,
} from '@apexinternal/ui-components'
import {
  Box,
  FormControl,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useFormik } from 'formik'
import { get, isEmpty } from 'lodash'
import { updateAddressSchema } from './UpdateAddressSchema'
import { usStatesList } from '../../../../constants'
import { sentenceCase } from '../../../../utils'
import { Addresses } from 'types/CommonTypes'

export interface EditModalContentProps {
  accountAddress: Addresses
  accountNumber: string
  formatResponse: (values: any) => void
  hideHomeAddress: boolean
  onClose: () => void
  setHomeAddress: (bool: boolean) => void
  setConfirmationView: (bool: boolean) => void
}

const EditModalContent = ({
  accountAddress,
  formatResponse,
  hideHomeAddress,
  onClose,
  setHomeAddress,
  setConfirmationView,
}: EditModalContentProps) => {
  const formik = useFormik({
    initialValues: {
      mailingAddress: {
        streetAddress: {
          0: sentenceCase(accountAddress.mailingAddress.streetAddress[0]),
          1: sentenceCase(accountAddress.mailingAddress.streetAddress[1]),
        },
        city: sentenceCase(accountAddress.mailingAddress.city),
        state: accountAddress.mailingAddress.state,
        postalCode: accountAddress.mailingAddress.postalCode,
      },
      homeAddress: {
        toggleHomeAddress: hideHomeAddress,
        streetAddress: {
          0: accountAddress.homeAddress
            ? accountAddress.homeAddress.streetAddress[0]
            : '',
          1: accountAddress.homeAddress
            ? accountAddress.homeAddress.streetAddress[1]
            : '',
        },
        city: accountAddress.homeAddress ? accountAddress.homeAddress.city : '',
        state: accountAddress.homeAddress
          ? accountAddress.homeAddress.state
          : '',
        postalCode: accountAddress.homeAddress
          ? accountAddress.homeAddress.postalCode
          : '',
      },
    } as {
      mailingAddress: {
        streetAddress: { 0: string; 1: string }
        city: string
        state: string
        postalCode: string
      }
      homeAddress: {
        toggleHomeAddress: boolean
        streetAddress: { 0: string; 1: string }
        city: string
        state: string
        postalCode: string
      }
    },
    onSubmit: () => {
      formatResponse(formik.values)
      setConfirmationView(true)
    },
    validationSchema: updateAddressSchema,
  })

  const isSubmitDisabled = !isEmpty(formik.errors)

  const handleSubmit = (): void => {
    formik.submitForm().catch(() => console.error('Formik error'))
  }

  const handleBlur = (e: { target: { name: string } }) => {
    formik.setFieldTouched(e.target.name)
  }

  const toggleCheckbox = (e: any) => {
    setFieldValue(
      'homeAddress.toggleHomeAddress',
      e.target.checked ? true : false
    )
    setHomeAddress(e.target.checked)
  }

  const setFieldValue = formik.setFieldValue
  const formikTouched = formik.touched
  const formikErrors = formik.errors

  const getFieldError = (fieldPath: string) =>
    get(formikTouched, fieldPath) ? get(formikErrors, fieldPath) : undefined

  const mailingStreetError = getFieldError('mailingAddress.streetAddress[0]')
  const mailingCityError = getFieldError('mailingAddress.city')
  const mailingPostalCodeError = getFieldError('mailingAddress.postalCode')

  const homeCityError = getFieldError('homeAddress.city')
  const homePostalCodeError = getFieldError('homeAddress.postalCode')
  // home street address cannot use formik validation due to nested elements
  const [streetAddressTouched, setStreetAddressTouched] = useState(false)
  const isInvalid =
    streetAddressTouched && !formik.values.homeAddress.streetAddress[0]
  const [homeStateTouched, setHomeStateTouched] = useState(false)
  const invalidHomeState = homeStateTouched && !formik.values.homeAddress.state

  return (
    <ModalContent>
      <form>
        <ModalHeader>Update Address</ModalHeader>
        <ModalCloseButton onClick={onClose} />
        <ModalBody>
          <Text as="h4" textStyle="h4" fontWeight="semibold">
            Mailing Address *
          </Text>
          <SimpleGrid columns={2} spacing={2}>
            <Box>
              <FormControl>
                <LabeledInput
                  charCounter={30}
                  data-testid="street-address-input"
                  errorMessage={mailingStreetError || undefined}
                  isInvalid={!!mailingStreetError}
                  isRequired
                  label="Street Address"
                  maxLength={30}
                  name="mailingAddress.streetAddress[0]"
                  onBlur={handleBlur}
                  onChange={formik.handleChange}
                  size="md"
                  value={formik.values.mailingAddress.streetAddress[0] || ''}
                />
              </FormControl>
            </Box>
            <Box>
              <FormControl>
                <LabeledInput
                  charCounter={30}
                  label="Street Address 2"
                  maxLength={30}
                  name="mailingAddress.streetAddress[1]"
                  onBlur={handleBlur}
                  onChange={formik.handleChange}
                  size="md"
                  value={formik.values.mailingAddress.streetAddress[1] || ''}
                />
              </FormControl>
            </Box>
            <Box>
              <FormControl>
                <LabeledInput
                  errorMessage={mailingCityError || undefined}
                  isInvalid={!!mailingCityError}
                  isRequired
                  label="City"
                  name="mailingAddress.city"
                  onBlur={handleBlur}
                  onChange={formik.handleChange}
                  size="md"
                  value={formik.values.mailingAddress.city || ''}
                />
              </FormControl>
            </Box>
            <Box>
              <FormControl>
                <LabeledSelect
                  errorMessage={'Required'}
                  isInvalid={!formik.values.mailingAddress.state}
                  isRequired
                  label="State"
                  name="mailingAddress.state"
                  onChange={formik.handleChange}
                  value={formik.values.mailingAddress.state || ''}
                >
                  <option value=""></option>
                  {usStatesList.map((state, index) => (
                    <option key={`${index}${state.key}`} value={state.key}>
                      {state.name}
                    </option>
                  ))}
                </LabeledSelect>
              </FormControl>
            </Box>
            <Box>
              <FormControl>
                <LabeledInput
                  errorMessage={mailingPostalCodeError || undefined}
                  isInvalid={!!mailingPostalCodeError}
                  isRequired
                  label="ZIP Code"
                  maxLength={10}
                  name="mailingAddress.postalCode"
                  onBlur={handleBlur}
                  onChange={formik.handleChange}
                  size="md"
                  value={formik.values.mailingAddress.postalCode || ''}
                />
              </FormControl>
            </Box>
          </SimpleGrid>
          <Checkbox
            isChecked={hideHomeAddress}
            fontWeight={400}
            name="homeAddress.toggleHomeAddress"
            onChange={e => toggleCheckbox(e)}
            pt={5}
            pb={5}
          >
            Home address is the same as the mailing address
          </Checkbox>
          {!formik.values.homeAddress.toggleHomeAddress && (
            <>
              <Text as="h4" textStyle="h4">
                Home Address (optional)
              </Text>
              <SimpleGrid columns={2} spacing={2}>
                <Box>
                  <FormControl>
                    <LabeledInput
                      charCounter={30}
                      errorMessage={isInvalid ? 'Required' : undefined}
                      isInvalid={isInvalid}
                      isRequired
                      label="Street Address"
                      maxLength={30}
                      name="homeAddress.streetAddress[0]"
                      onBlur={() => setStreetAddressTouched(true)}
                      onChange={formik.handleChange}
                      size="md"
                      value={formik.values.homeAddress.streetAddress[0] || ''}
                    />
                  </FormControl>
                </Box>
                <Box>
                  <FormControl>
                    <LabeledInput
                      charCounter={30}
                      label="Street Address 2"
                      maxLength={30}
                      name="homeAddress.streetAddress[1]"
                      onBlur={handleBlur}
                      onChange={formik.handleChange}
                      size="md"
                      value={formik.values.homeAddress.streetAddress[1] || ''}
                    />
                  </FormControl>
                </Box>
                <Box>
                  <FormControl>
                    <LabeledInput
                      errorMessage={homeCityError || undefined}
                      isInvalid={!!homeCityError}
                      isRequired
                      label="City"
                      name="homeAddress.city"
                      onBlur={handleBlur}
                      onChange={formik.handleChange}
                      size="md"
                      value={formik.values.homeAddress.city || ''}
                    />
                  </FormControl>
                </Box>
                <Box>
                  <FormControl>
                    <LabeledSelect
                      errorMessage={'Required'}
                      isInvalid={invalidHomeState}
                      isRequired
                      label="State"
                      name="homeAddress.state"
                      onBlur={() => setHomeStateTouched(true)}
                      onChange={formik.handleChange}
                      value={formik.values.homeAddress.state || ''}
                    >
                      <option value=""></option>
                      {usStatesList.map((state, index) => (
                        <option key={`${index}${state.key}`} value={state.key}>
                          {state.name}
                        </option>
                      ))}
                    </LabeledSelect>
                  </FormControl>
                </Box>
                <Box>
                  <FormControl>
                    <LabeledInput
                      errorMessage={homePostalCodeError || undefined}
                      isInvalid={!!homePostalCodeError}
                      isRequired
                      label="ZIP Code"
                      maxLength={10}
                      name="homeAddress.postalCode"
                      onBlur={handleBlur}
                      onChange={formik.handleChange}
                      size="md"
                      value={formik.values.homeAddress.postalCode || ''}
                    />
                  </FormControl>
                </Box>
              </SimpleGrid>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Stack direction="row" spacing={2}>
            <Button variant="white" onClick={onClose} tabIndex={1}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={isSubmitDisabled}
              onClick={handleSubmit}
            >
              Confirm Changes
            </Button>
          </Stack>
        </ModalFooter>
      </form>
    </ModalContent>
  )
}

export default EditModalContent
